import type { EmailDelivery as GQLItem, EmailDeliveryConnection as GQLConnection } from '../../../generated/internal.graphql';
import { take } from '../../../util/takePayload';
import * as GqlConnection from '../../connection/GqlConnection';
import * as PageInfo from '../../connection/PageInfo';
import * as Account from '../Account/Account';
import type { EmailDeliveryState } from './EmailDeliveryState';

/**
 * One staff-visible email send, as the deliverability report reads it: who it went to, what state it
 * reached, and — when it failed — the terminal SendGrid event behind that.
 *
 * Every field the schema exposes is mapped, because every one of them is a column the report shows;
 * there is no lighter row shape worth splitting out. `events` — the raw SendGrid jsonb — is not on
 * the schema at all: the backend distils it into `terminalEventType` / `terminalEventReason` so no
 * caller has to parse it.
 *
 * `account` goes through the shared `AccountSummary` mapper rather than a local copy, so a
 * deliverability row links to the same account record the staff account screens read.
 */
export type EmailDelivery = {
  id: number;
  to: string;
  subject: string;
  state: EmailDeliveryState;
  createdAt: string;
  account?: Account.Account;
  mailStream?: string;
  recipientDomain?: string;
  sentAt?: string;
  deliveredAt?: string;
  terminalEventType?: string;
  terminalEventReason?: string;
};

export type EmailDeliveryConnection = GqlConnection.GqlConnection<EmailDelivery>;

/**
 * The nullable fields normalise to `undefined`: nothing in the admin renders a `null`, and a `v-if`
 * on one prints the string rather than falling through to the empty branch. They are nullable for
 * real reasons, not defensively — an unattributed send has no `account`, a pending one has no
 * `sentAt`, and a delivered one has no terminal failure.
 */
export const fromGql = (item: GQLItem): EmailDelivery => ({
  ...take<EmailDelivery>(item, 'id', 'to', 'subject', 'state', 'createdAt'),
  account: item.account ? Account.fromGql(item.account) : undefined,
  mailStream: item.mailStream ?? undefined,
  recipientDomain: item.recipientDomain ?? undefined,
  sentAt: item.sentAt ?? undefined,
  deliveredAt: item.deliveredAt ?? undefined,
  terminalEventType: item.terminalEventType ?? undefined,
  terminalEventReason: item.terminalEventReason ?? undefined,
});

/**
 * Mapped by hand rather than through `GqlConnection.fromGql`, for the two reasons
 * `Internal/TicketLayout` documents and that hold identically here:
 *
 * - `edges` and `node` are both nullable on the schema. graphql-ruby does not emit those nulls for
 *   this resolver, but casting them away would let a null node reach `fromGql` and throw "Cannot
 *   read properties of null" — which the caller's `try`/`catch` could then only report as an opaque
 *   failure. (`pageInfo` is non-null and is still dereferenced, so this is the same soft-failing
 *   posture as the siblings, not a throw-free guarantee.)
 * - The query selects `edges` only, so the shared helper would leave `nodes` permanently `[]` while
 *   `GqlConnection<T>` promises a populated array. Deriving it from the edges keeps that promise.
 */
export const fromGqlConnection = (connection: GQLConnection): EmailDeliveryConnection => {
  const edges = (connection.edges ?? [])
    .filter(edge => Boolean(edge?.node))
    .map(edge => ({ cursor: edge!.cursor, node: fromGql(edge!.node as GQLItem) }));

  return {
    edges,
    nodes: edges.map(edge => edge.node),
    pageInfo: PageInfo.fromGql(connection.pageInfo),
  };
};
